import React from "react";
import QuickViewCell from "./QuickViewCell";
import { plainTitle } from "../utility/quickViewMapper";

/**
 * Renders one question's row: its index, title (with unit) and a value cell
 * per period.
 *
 * @param {Object} props
 * @param {Object} props.question - The question being rendered.
 * @param {number} props.index - Zero-based index within its module, used for
 *   the displayed number and row striping.
 * @param {Array<{key: string}>} props.periods - Ordered period columns.
 * @param {Object<string, Object>} props.quickViewMap - questionId -> bucket key -> cell.
 * @param {Object<string, string>} [props.unitSettingsMap={}] - questionId -> unit
 *   fallback, used when the question has no answer cell carrying a unit.
 * @returns {JSX.Element} A table row.
 */
const QuickViewRow = ({ question, index, periods, quickViewMap, unitSettingsMap = {} }) => {
  const questionAnswers = quickViewMap[question.questionId] || {};

  /**
   * The unit shown once beside the title (it is constant per question - same
   * metric - so it is not repeated in every answer cell). Primary source is the
   * unit the analytics row was converted to; falls back to the selectedUnit from
   * getUnitKpiSettings (unitSettingsMap) when the question has no answers yet,
   * and null when neither is available.
   */
  const unit =
    Object.values(questionAnswers).find((cell) => cell?.unit)?.unit ||
    unitSettingsMap[question.questionId] ||
    null;

  return (
    <tr className={index % 2 === 0 ? "qv-row--even" : "qv-row--odd"}>
      <td className="qv-td qv-td--index">{index + 1}</td>
      <td className="qv-td qv-td--question">
        <span style={{ whiteSpace: "pre-line" }}>{plainTitle(question.title)}</span>
        {unit && <span className="qv-unit">({unit})</span>}
      </td>
      {periods.map(({ key }) => (
        <QuickViewCell key={key} cell={questionAnswers[key]} />
      ))}
    </tr>
  );
};

export default QuickViewRow;
